"use client";

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {CitationSource} from "@/types/ask";
import {cn} from "@/lib/utils";
import {
   Collapsible,
   CollapsibleContent,
   CollapsibleTrigger,
} from "@/components/ui/collapsible";
import Icon from "@/components/library/Icon";
import {getDomain} from "@/utils/url";
import {useDocumentPreviewContext} from "@/hooks/useDocumentPreview";

interface SourceFooterProps {
   citations: CitationSource[];
}

const SourceFooter: React.FC<SourceFooterProps> = ({citations}) => {
   const [open, setOpen] = useState(false);
   const openDocument = useDocumentPreviewContext();
   const {t} = useTranslation("ask");

   if (citations.length === 0) return null;

   // Sort by citationNumber so footer order matches inline badges
   const sorted = [...citations].sort((a, b) => a.citationNumber - b.citationNumber);

   const handleDocClick = (c: CitationSource) => {
      if (c.documentId) {
         openDocument({
            id: c.documentId,
            fileName: c.fileName,
            pages: c.pages,
         });
      }
   };

   return (
      <Collapsible open={open} onOpenChange={setOpen} className="mt-4 font-source-serif">
         <CollapsibleTrigger
            className="group flex items-center gap-1 text-[12px] font-medium text-500 hover:text-700 transition-colors cursor-pointer select-none py-2 -my-2 pr-4"
         >
            <Icon
               icon="chevron-right"
               className={cn(
                  "w-3 h-3 transition-transform duration-150",
                  open ? "rotate-90" : "rotate-0"
               )}
            />
            {t("sources", {count: sorted.length})}
         </CollapsibleTrigger>

         <CollapsibleContent>
            <ol className="mt-1.5 flex flex-col">
               {sorted.map((c, idx) => {
                  const isWeb = !!c.url;
                  const isDoc = !isWeb && !!c.documentId;
                  const domain = isWeb ? getDomain(c.url!) : null;

                  const isInteractive = isWeb || isDoc;
                  const rowClassName = cn(
                     "group/row flex items-center gap-2 px-2 py-2 -mx-2 rounded-lg transition-colors min-w-0",
                     isInteractive && "hover:bg-200 cursor-pointer"
                  );

                  const rowContent = (
                     <>
                        <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-md bg-200 text-[10px] font-medium text-500 shrink-0 group-hover/row:bg-300 group-hover/row:text-700 transition-colors">
                           {c.citationNumber}
                        </span>

                        <span className="flex items-center gap-1.5 min-w-0 text-[12px]">
                           <span className="font-medium text-700 group-hover/row:text-900 transition-colors shrink-0">
                              {domain ?? (c.fileName ?? c.title)}
                           </span>
                           {c.title && (domain ?? c.fileName) && (
                              <>
                                 <span className="text-300 shrink-0">·</span>
                                 <span className="text-500 truncate">{c.title}</span>
                              </>
                           )}
                           {c.pages && c.pages.length > 0 && (
                              <span className="text-400 shrink-0 text-[11px]">p.{c.pages.join(", ")}</span>
                           )}
                        </span>
                     </>
                  );

                  const renderRow = () => {
                     if (isWeb) {
                        return (
                           <a href={c.url} target="_blank" rel="noopener noreferrer" className={rowClassName}>
                              {rowContent}
                           </a>
                        );
                     }
                     if (isDoc) {
                        return (
                           <button type="button" onClick={() => handleDocClick(c)} className={cn(rowClassName, "w-full text-left")}>
                              {rowContent}
                           </button>
                        );
                     }
                     return <div className={rowClassName}>{rowContent}</div>;
                  };

                  return (
                     <li key={`${c.citationNumber}-${idx}`}>
                        {renderRow()}
                     </li>
                  );
               })}
            </ol>
         </CollapsibleContent>
      </Collapsible>
   );
};

export default SourceFooter;
